import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { KIcon } from '@karnott/icons';
import { pixelSpacing } from '@karnott/theme';
import { Field } from '../field';
import { ResetButton } from '../reset-button';
import { Stepper } from '../stepper';
import { Popup } from './popup';

export type NumberRange = {
  start: number;
  end: number;
};

const COMMIT_DELAY_MS = 100;

function clampRange({ start, end }: NumberRange, min: number, max: number): NumberRange {
  return { start: Math.min(Math.max(start, min), max), end: Math.max(Math.min(end, max), min) };
}

type Props = {
  /** The handler’s label, called with the range when it is narrower than `[min, max]` */
  getTitle: (value?: NumberRange) => string;
  /** The lowest selectable value */
  min: number;
  /** The highest selectable value. With `Infinity`, an empty max input means no upper bound */
  max: number;
  /** The selected range, displayed clamped to `[min, max]` */
  value: NumberRange;
  /** Called with the new range 100ms after the last step or typed value */
  onValueChange: (value: NumberRange) => void;
  /** Whether the popup can be opened */
  disabled?: boolean;
  /** Karnott icon to display in the trigger button */
  Icon?: KIcon;
};

/** A popup to choose a numeric range with two steppers */
export function RangePopup({ getTitle, min, max, value, onValueChange, disabled = false, Icon }: Props) {
  const [pendingRange, setPendingRange] = useState<NumberRange>();
  const commitTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(commitTimeout.current), []);

  const range = clampRange(pendingRange ?? value, min, max);
  const showSelection = !disabled && (range.start > min || range.end < max);

  const commit = (nextRange: NumberRange) => {
    setPendingRange(nextRange);
    clearTimeout(commitTimeout.current);
    commitTimeout.current = setTimeout(() => {
      onValueChange(nextRange);
      setPendingRange(undefined);
    }, COMMIT_DELAY_MS);
  };

  return (
    <Popup
      title={showSelection ? getTitle(range) : getTitle()}
      showSelection={showSelection}
      disabled={disabled}
      Icon={Icon}
    >
      <Steppers>
        <Field label="Min">
          <BoundRow>
            <StepperContainer>
              <Stepper
                label="Minimum"
                value={range.start}
                min={min}
                max={range.end}
                setValue={(start) => commit({ ...range, start: Number.isNaN(start) ? min : start })}
              />
            </StepperContainer>
            <ResetButton
              label="Reset minimum"
              disabled={range.start <= min}
              onClick={() => commit({ ...range, start: min })}
            />
          </BoundRow>
        </Field>
        <Field label="Max">
          <BoundRow>
            <StepperContainer>
              <Stepper
                label="Maximum"
                value={Number.isFinite(range.end) ? range.end : NaN}
                min={range.start}
                max={max}
                setValue={(end) => commit({ ...range, end: Number.isNaN(end) ? max : end })}
              />
            </StepperContainer>
            <ResetButton
              label="Reset maximum"
              disabled={range.end >= max}
              onClick={() => commit({ ...range, end: max })}
            />
          </BoundRow>
        </Field>
      </Steppers>
    </Popup>
  );
}

const Steppers = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pixelSpacing('small')};
`;

const BoundRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${pixelSpacing('small')};
`;

const StepperContainer = styled.div`
  width: 11rem;
`;
